import { Euler, Quaternion, Vector3 } from '@technology/web3d';
import type { Image, Tileset } from '@data-access/geospatial/imaging';
import type {
  Station,
  StationTilesetInfo,
  StationTransform,
} from '@data-access/geospatial/scan-data';
import { degreesToRadians } from '@trimble/utilities';

const ABSOLUTE_URL_PATTERN = /^(?:https?:)?\/\//i;

/**
 * SDS returns a **base** `tilesetUrl` (CDN path + signed query) and a separate
 * `tileTemplate` (relative pattern, e.g. `{level}_{y}_{x}.jpg`). The imaging API
 * instead returns a single combined template URL. Join SDS pieces the same way
 * consumers expect:
 *
 * `basePath + '/' + tileTemplate + '?' + query` when both are present.
 *
 * File Service backed tilesets instead return the complete signed template in
 * both fields. Joining those yields a doubled URL whose path was never signed,
 * so File Service answers 403 — an absolute `tileTemplate` is therefore final.
 */
export function resolveSdsStationTilesetTemplateUrl(
  info: StationTilesetInfo | undefined
): string | undefined {
  if (!info) {
    return undefined;
  }
  const baseUrl = info.tilesetUrl?.trim();
  const relTemplate = info.tileTemplate?.trim();

  if (relTemplate && ABSOLUTE_URL_PATTERN.test(relTemplate)) {
    return relTemplate;
  }

  if (baseUrl && relTemplate) {
    const qIdx = baseUrl.indexOf('?');
    const basePath =
      qIdx >= 0
        ? baseUrl.slice(0, qIdx).replace(/\/$/, '')
        : baseUrl.replace(/\/$/, '');
    const query = qIdx >= 0 ? baseUrl.slice(qIdx + 1) : '';
    const pathSuffix = relTemplate.replace(/^\//, '');
    const combined = `${basePath}/${pathSuffix}`;
    return query.length > 0 ? `${combined}?${query}` : combined;
  }

  return relTemplate || baseUrl || undefined;
}

/** Imaging / File Service tiler: `.../{level}/{x}/{y}.jpg` directory layout. */
const PATH_STYLE_TEMPLATE_PATTERN = /\{(?:level|z)\}\/\{x\}\/\{y\}/i;

/**
 * Which tiler produced the tileset, not which API listed it: the legacy SDS
 * tiler writes flat `{level}_{y}_{x}.jpg` filenames numbered from 1, while the
 * imaging / File Service tiler writes a `{level}/{x}/{y}` directory tree
 * numbered from 0. SDS serves both, so the template layout is the only in-band
 * signal (`StationTilesetInfo` carries no level-base field).
 *
 * Getting this wrong shifts every LOD by one level, so the viewer indexes a
 * finer grid with the coarser grid's tile range and draws only the top-left
 * quadrant of the equirectangular image — or requests a level that does not
 * exist and the panorama pane never leaves the spinner.
 */
// TODO(MERCATOR-294): Hacky URL-layout guess. Replace with an explicit 0/1-based
// flag from SDS when there is time for a proper contract.
export function isSdsTilesetUrlLevelZeroBased(
  info: StationTilesetInfo | undefined
): boolean {
  const templateUrl = resolveSdsStationTilesetTemplateUrl(info);
  return (
    templateUrl !== undefined && PATH_STYLE_TEMPLATE_PATTERN.test(templateUrl)
  );
}

function synthesizeTilesetLevels(
  levelCount: number | undefined
): Tileset['levels'] {
  if (levelCount === undefined || levelCount < 1) {
    return null;
  }
  return Array.from({ length: levelCount }, (_, index) => ({ level: index }));
}

type EulerOrder = 'XYZ' | 'YXZ' | 'ZXY' | 'ZYX' | 'YZX' | 'XZY';
const FULL_CIRCLE_DEGREES = 360;
const HALF_TURN_DEGREES = 180;
const QUATERNION_FACTOR = 2;

const Z_AXIS = new Vector3(0, 0, 1);

/**
 * Builds a unit quaternion for a pure rotation of `degrees` around the
 * (un-permuted) world Z axis. Composed from the codebase's shared
 * primitives: `degreesToRadians` + Three.js `setFromAxisAngle` — which
 * does the canonical half-angle math internally — so every consumer
 * stays in lock-step (no risk of a sin/cos / half-angle typo drifting
 * one site away from the others).
 *
 * Caller is responsible for any input normalization or rounding
 * (e.g. snapping to whole degrees before persisting to SDS).
 */
export function zAxisQuaternionFromDegrees(degrees: number): Quaternion {
  return new Quaternion().setFromAxisAngle(Z_AXIS, degreesToRadians(degrees));
}

function normalizeHeadingDegrees(degrees: number): number {
  return (
    ((degrees % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) %
    FULL_CIRCLE_DEGREES
  );
}

function isStationQuaternionDefined(
  quaternion: Station['quaternion'] | StationTransform['quaternion'] | undefined
): quaternion is { x: number; y: number; z: number; w: number } {
  return (
    quaternion !== undefined &&
    quaternion.x !== undefined &&
    quaternion.y !== undefined &&
    quaternion.z !== undefined &&
    quaternion.w !== undefined
  );
}

function asEulerOrder(rotationOrder: string | undefined): EulerOrder {
  const order = rotationOrder?.toUpperCase();
  switch (order) {
    case 'XYZ':
    case 'YXZ':
    case 'ZXY':
    case 'ZYX':
    case 'YZX':
    case 'XZY':
      return order;
    default:
      return 'XYZ';
  }
}

export function calculateStationHeadingOffset(
  stationTransform: StationTransform | undefined
): number {
  const quaternion = stationTransform?.quaternion;
  if (!isStationQuaternionDefined(quaternion)) {
    return 0;
  }
  const sinyCosp =
    QUATERNION_FACTOR *
    (quaternion.w * quaternion.z + quaternion.x * quaternion.y);
  const cosyCosp =
    1 -
    QUATERNION_FACTOR *
      (quaternion.y * quaternion.y + quaternion.z * quaternion.z);
  const yawRadians = Math.atan2(sinyCosp, cosyCosp);
  const yawDegrees = Math.round((yawRadians * HALF_TURN_DEGREES) / Math.PI);
  return normalizeHeadingDegrees(yawDegrees);
}

/**
 * Builds a {@link StationTransform} whose quaternion represents a pure
 * Z-axis rotation of `headingOffsetDegrees` (heading is rounded to the
 * nearest whole degree for SDS persistence).
 *
 * Verified by `sds-station-mapper.spec.ts` to round-trip with
 * `calculateStationHeadingOffset` for cardinal headings.
 */
export function getStationTransformWithHeadingOffset(
  stationTransform: StationTransform | undefined,
  headingOffsetDegrees: number
): StationTransform {
  const normalized = normalizeHeadingDegrees(headingOffsetDegrees);
  const { x, y, z, w } = zAxisQuaternionFromDegrees(Math.round(normalized));

  return {
    ...stationTransform,
    quaternion: { x, y, z, w },
  };
}

function applyHeadingToQuaternion(
  q: { x: number; y: number; z: number; w: number },
  stationTransform: StationTransform | undefined
): Image['quaternionOrientation'] {
  const headingOffsetDegrees = calculateStationHeadingOffset(stationTransform);

  if (headingOffsetDegrees === 0) {
    return { x: q.x, y: q.y, z: q.z, w: q.w };
  }

  // Un-permute to reverse LodAnalyzer's (x,y,z,w) -> (z,w,x,y) pre-permutation
  const unpermuted = new Quaternion(q.z, q.w, q.x, q.y);

  // Heading offset as a Z-axis rotation (Z is vertical in the post-conversion frame)
  const headingQuat = zAxisQuaternionFromDegrees(headingOffsetDegrees);
  const rotated = headingQuat.multiply(unpermuted);

  // Re-permute for LodAnalyzer
  return {
    x: rotated.z,
    y: rotated.w,
    z: rotated.x,
    w: rotated.y,
  };
}

function convertLegacyEulerToQuaternion(
  station: Station,
  stationTransform: StationTransform | undefined
): Image['quaternionOrientation'] {
  const DEG2RAD = Math.PI / HALF_TURN_DEGREES;
  const headingOffsetDegrees = calculateStationHeadingOffset(stationTransform);
  const heading = (station.orientation?.yaw ?? 0) + headingOffsetDegrees;
  const pitch = station.orientation?.pitch ?? 0;
  const roll = station.orientation?.roll ?? 0;
  const order = asEulerOrder(station.orientation?.rotationOrder);

  const eulerQuat = new Quaternion().setFromEuler(
    new Euler(roll * DEG2RAD, pitch * DEG2RAD, heading * DEG2RAD, order)
  );

  // Y-up to Z-up conversion (90deg X-axis rotation) — matches legacy euler path
  const yUpToZUp = new Quaternion(Math.SQRT1_2, 0, 0, Math.SQRT1_2);
  const result = eulerQuat.clone().multiply(yUpToZUp);

  // Inverse permutation to cancel LodAnalyzer's (x,y,z,w) -> (z,w,x,y)
  return {
    x: result.z,
    y: result.w,
    z: result.x,
    w: result.y,
  };
}

/**
 * Prefers the station quaternion whenever SDS provides one. It is the same
 * value the imaging API returns as `quaternionOrientation`, so SDS and imaging
 * panoramas of one image render identically. SDS also echoes the raw
 * yaw/pitch/roll, and re-deriving the rotation from those with the legacy
 * Euler path turns panoramas by up to 180°. That path stays only as the
 * fallback for stations that carry Euler angles alone.
 */
function getStationQuaternionOrientation(
  station: Station,
  stationTransform: StationTransform | undefined
): Image['quaternionOrientation'] {
  if (isStationQuaternionDefined(station.quaternion)) {
    return applyHeadingToQuaternion(station.quaternion, stationTransform);
  }
  return convertLegacyEulerToQuaternion(station, stationTransform);
}

export function mapSdsStationToImagingImage(
  station: Station,
  stationTransform?: StationTransform
): Partial<Image> | null {
  if (!station.id || !station.position) {
    return null;
  }
  if (station.status !== 'Ready') {
    return null;
  }

  const info = station.tilesetInfo;
  const templateUrl = resolveSdsStationTilesetTemplateUrl(info);
  if (templateUrl === undefined) {
    return null;
  }

  const resizedWidth = info?.resizedWidth ?? 0;
  const resizedHeight = info?.resizedHeight ?? 0;

  const tileset: Tileset & { readonly isUrlLevelZeroBased: boolean } = {
    templateUrl,
    tileWidth: info?.tileWidth,
    tileHeight: info?.tileHeight,
    boundingBox: {
      minX: 0,
      maxY: 0,
      maxX: resizedWidth,
      minY: -resizedHeight,
    },
    levels: synthesizeTilesetLevels(info?.levels),
    mimeType: null,
    extension: null,
    isUrlLevelZeroBased: isSdsTilesetUrlLevelZeroBased(info),
  };

  const quaternionOrientation = getStationQuaternionOrientation(
    station,
    stationTransform
  );

  return {
    id: station.id,
    path: station.name ?? undefined,
    position: station.position,
    fov: station.fov,
    orientation: station.orientation,
    quaternionOrientation,
    imageInfo: station.imageInfo,
    undistortedCalibration: station.calibration,
    tileset,
    captureUtc: station.captureUtc ?? null,
  };
}

/**
 * Ready SDS stations with tile URLs, mapped as panorama images.
 *
 * SDS stations are treated as panorama-only in this app, even when calibration
 * metadata exists on the station payload.
 */
export function mapSdsStationsToImagingImages(
  stations: ReadonlyArray<Station>,
  stationTransform?: StationTransform
): Partial<Image>[] {
  const panoramaImages: Partial<Image>[] = [];

  for (const station of stations) {
    const image = mapSdsStationToImagingImage(station, stationTransform);
    if (!image) {
      continue;
    }
    panoramaImages.push(image);
  }

  return panoramaImages;
}
